import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import {
  getCurrentRegion,
  setCurrentRegion as setRegionModule,
  getRegionConfig,
  getEezUrl,
  getMpaUrl,
  getIsoParalitoralUrl
} from '../regions/regionConfig';
import { useAuth } from './AuthContext';

const RegionContext = createContext(null);

export const RegionProvider = ({ children }) => {
  const { authStatus, allowedRegions, isSuperuser } = useAuth();
  const [region, setRegionState] = useState(() => {
    const saved = getCurrentRegion();
    if (allowedRegions.length > 0 && !isSuperuser) {
      return allowedRegions.includes(saved) ? saved : (allowedRegions[0] || saved);
    }
    return saved;
  });

  useEffect(() => {
    if (authStatus === 'authenticated' && allowedRegions.length > 0 && !isSuperuser) {
      if (!allowedRegions.includes(region)) {
        const defaultRegion = allowedRegions[0];
        setRegionModule(defaultRegion);
        setRegionState(defaultRegion);
      }
    }
  }, [authStatus, allowedRegions, isSuperuser]);

  const changeRegion = useCallback((regionId) => {
    if (!isSuperuser && allowedRegions.length > 0 && !allowedRegions.includes(regionId)) {
      return;
    }
    setRegionModule(regionId);
    setRegionState(regionId);
  }, [allowedRegions, isSuperuser]);

  const config = getRegionConfig(region);

  const value = {
    region,
    regionId: region,
    regionName: config?.name || '',
    regionBbox: config?.bbox || null,
    regionPorts: config?.ports || [],
    regionTideStations: config?.tideStations || [],
    regionTimezone: config?.timezone || 'UTC',
    regionConfig: config,
    regionEezUrl: getEezUrl(region),
    regionMpaUrl: getMpaUrl(region),
    regionIsoParalitoralUrl: getIsoParalitoralUrl(region),
    changeRegion,
    allowedRegions: isSuperuser ? null : allowedRegions,
  };

  return (
    <RegionContext.Provider value={value}>
      {children}
    </RegionContext.Provider>
  );
};

export const useRegion = () => {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return context;
};

export default RegionContext;
